import { useCallback } from 'react';
import { CreateIdeaStopFormData } from '../../../../../../types/ideaStop.types';
import { IDEA_STOP_UI_TO_API_FIELD_MAP } from '../../../../../../types/ideaStop.config';
import { combineDateAndTime } from '../../../../../../utils/dateUtils';
import { convertAddressToApiFormat } from '../../../../TripDetails/utils/addressUtils';

/**
 * Hook for converting form data to API payload for idea stops
 * @param ideaId - The idea ID to associate the idea stop with
 * @returns Object with convertToApiPayload function
 */
export const useIdeaStopPayload = (ideaId: number) => {
  const convertToApiPayload = useCallback(
    (values: CreateIdeaStopFormData) => {
      const ideaStopType = values.ideaStopType;
      const apiData: any = {
        idea: ideaId,
        description: values.description,
      };

      if (values.startDate && values.startTime) {
        apiData.start_date_time = combineDateAndTime(values.startDate, values.startTime);
      } else if (values.departureDate && values.departureTime) {
        apiData.start_date_time = combineDateAndTime(values.departureDate, values.departureTime);
      } else if (values.checkInDate && values.checkInTime) {
        apiData.start_date_time = combineDateAndTime(values.checkInDate, values.checkInTime);
      }

      if (values.airline_details) {
        apiData.airline = values.airline_details.name;
        apiData.airline_iata_code = values.airline_details.iata_code;
        console.log('Airline details:', values.airline_details);
      } else if (values.airline) {
        apiData.airline_iata_code = values.airline;
        console.log('Only airline iata_code available:', values.airline);
      }

      if (values.flightFrom_details) {
        apiData.flight_from = values.flightFrom_details.iata_code;
        console.log('Flight from details:', values.flightFrom_details);
      } else if (values.flightFrom) {
        apiData.flight_from = values.flightFrom;
        console.log('Only flight from iata_code available:', values.flightFrom);
      }

      if (values.flightTo_details) {
        apiData.flight_to = values.flightTo_details.iata_code;
        console.log('Flight to details:', values.flightTo_details);
      } else if (values.flightTo) {
        apiData.flight_to = values.flightTo;
        console.log('Only flight to iata_code available:', values.flightTo);
      }

      if (ideaStopType === 'CarRental' && values.transportName) {
        apiData.transport_name = values.transportName;
      }
      if (ideaStopType === 'Cruise' && values.shipName) {
        apiData.transport_name = values.shipName;
      }

      Object.keys(values).forEach(uiField => {
        const apiField = IDEA_STOP_UI_TO_API_FIELD_MAP[uiField] || uiField;
        const value = values[uiField as keyof CreateIdeaStopFormData];

        if (
          value !== '' &&
          value !== null &&
          value !== undefined &&
          !uiField.includes('_details') &&
          !['ideaStopType', 'description'].includes(uiField) &&
          !uiField.includes('Date') &&
          !uiField.includes('Time') &&
          !['airline', 'flightFrom', 'flightTo', 'transportName', 'shipName'].includes(uiField)
        ) {
          if (uiField === 'vehicleType') {
            if (ideaStopType === 'Transportation') {
              apiData[apiField] = value;
            }
            return;
          }

          apiData[apiField] = value;
        }
      });

      const dateTimeFields = [
        { date: 'departureDate', time: 'departureTime', api: 'departure_date_time' },
        { date: 'arrivalDate', time: 'arrivalTime', api: 'arrival_date_time' },
        { date: 'endDate', time: 'endTime', api: 'end_date_time' },
        { date: 'checkInDate', time: 'checkInTime', api: 'check_in_date_time' },
        { date: 'checkOutDate', time: 'checkOutTime', api: 'check_out_date_time' },
      ];

      dateTimeFields.forEach(({ date, time, api }) => {
        const dateValue = values[date as keyof CreateIdeaStopFormData] as string;
        const timeValue = values[time as keyof CreateIdeaStopFormData] as string;

        if (dateValue && timeValue) {
          apiData[api] = combineDateAndTime(dateValue, timeValue);
        }
      });

      const addressFields = [
        { ui: 'address', api: 'address' },
        { ui: 'departureAddress', api: 'departure_address' },
        { ui: 'arrivalAddress', api: 'arrival_address' },
        { ui: 'startingPortAddress', api: 'starting_port_address' },
        { ui: 'endingPortAddress', api: 'ending_port_address' },
      ];

      addressFields.forEach(({ ui, api }) => {
        const addressValue = values[ui as keyof CreateIdeaStopFormData] as string;
        const detailsValue = values[`${ui}_details` as keyof CreateIdeaStopFormData] as google.maps.places.PlaceResult;

        if (addressValue) {
          const addressData = convertAddressToApiFormat(addressValue, detailsValue);
          if (addressData) {
            apiData[api] = addressData;
          }
        }
      });

      if (values.email && values.email.trim() !== '') {
        apiData.email = values.email;
      }
      if (values.phone && values.phone.trim() !== '') {
        apiData.phone = values.phone;
      }
      if (values.link && values.link.trim() !== '') {
        apiData.link = values.link;
      }

      const finalPayload = {
        type: ideaStopType,
        ideaStopData: apiData,
      };

      console.log('Final idea stop API payload:', finalPayload);
      return finalPayload;
    },
    [ideaId]
  );

  return { convertToApiPayload };
};
